// ─────────────────────────────────────────────────────────────────────────────
// SessionGoalPie
//
// A ring-of-wedges widget for the Monitor's left flanking tile. The rower
// picks a target BEFORE the session starts (duration in minutes, target
// type = split | rating | speed, and a numeric target value).
//
// Geometry
//   • The ring always shows EXACTLY 10 wedges, regardless of duration.
//     Each wedge represents `durationMin ÷ 10` of session time.
//   • Each wedge is split RADIALLY into an inner and an outer band:
//     — inner band = first half of the chunk
//     — outer band = second half of the chunk
//     So we score 20 half-chunks over the session.
//     (Radial vs. angular split was our call — radial keeps the 10-wedge
//     silhouette readable while still exposing sub-chunk resolution.)
//
// Colour scheme (updated per brief)
//   • orange  — target met
//   • grey    — target missed
//   • outline — not yet reached (ring visible as a thin outline only)
//
// Interaction
//   • Default state: ring outline only. NO setup panel, NO filled wedges.
//   • Tap the ring → setup panel appears (duration stepper, type chips,
//     target value stepper).
//   • Any tap inside the panel re-arms a 5-second auto-hide timer,
//     matching the Pace Target ghost-control pattern for consistency.
//   • Session starts → wedges fill in order. Tapping the ring during
//     the session reopens the setup for adjustment.
//
// Direction rules per target type (unchanged):
//   • split  → average ≤ target (lower = faster = better)
//   • rating → average ≥ target
//   • speed  → average ≥ target
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useRef, useState } from 'react';

const TARGET_TYPES = [
  { id: 'split',  label: 'Split /500m',    unit: 's/500m', direction: 'lower' },
  { id: 'rating', label: 'Stroke Rating',  unit: 'spm',    direction: 'higher' },
  { id: 'speed',  label: 'Speed',          unit: 'm/s',    direction: 'higher' },
];

function formatTargetValue(type, value) {
  if (type === 'split') {
    // value is stored as seconds
    const s = Math.max(0, Math.round(value));
    const m = Math.floor(s / 60);
    const rem = s % 60;
    return `${m}:${String(rem).padStart(2, '0')}`;
  }
  if (type === 'rating') return `${Math.round(value)}`;
  if (type === 'speed')  return `${Number(value).toFixed(2)}`;
  return String(value);
}

// Evaluate whether the average for a half-chunk passes the target.
// Returns 'met' (green), 'missed' (yellow), or 'stopped' (red — no
// rowing activity during the window).
function halfState(type, avg, hasActivity, target) {
  // No strokes / no motion during this window → the rower stopped
  // (bathroom break, chat, phone call). Paint the wedge red so a
  // later glance at the ring shows exactly when the row broke down.
  if (!hasActivity) return 'stopped';
  if (avg == null || !Number.isFinite(avg)) return 'missed';
  let passed = false;
  if (type === 'split')  passed = avg > 0 && avg <= target;
  else if (type === 'rating') passed = avg >= target;
  else if (type === 'speed')  passed = avg >= target;
  return passed ? 'met' : 'missed';
}

// Build the SVG path for an annular sector (a wedge with an inner cutout)
// spanning [startAngle, endAngle] clockwise from 12 o'clock, between the
// given inner and outer radii.
function annularSectorPath(cx, cy, rInner, rOuter, startAngle, endAngle) {
  const toRad = (deg) => (deg - 90) * (Math.PI / 180);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  const xo1 = cx + rOuter * Math.cos(toRad(startAngle));
  const yo1 = cy + rOuter * Math.sin(toRad(startAngle));
  const xo2 = cx + rOuter * Math.cos(toRad(endAngle));
  const yo2 = cy + rOuter * Math.sin(toRad(endAngle));
  const xi1 = cx + rInner * Math.cos(toRad(endAngle));
  const yi1 = cy + rInner * Math.sin(toRad(endAngle));
  const xi2 = cx + rInner * Math.cos(toRad(startAngle));
  const yi2 = cy + rInner * Math.sin(toRad(startAngle));
  return [
    `M ${xo1} ${yo1}`,
    `A ${rOuter} ${rOuter} 0 ${largeArc} 1 ${xo2} ${yo2}`,
    `L ${xi1} ${yi1}`,
    `A ${rInner} ${rInner} 0 ${largeArc} 0 ${xi2} ${yi2}`,
    'Z',
  ].join(' ');
}

// Fixed ring dimensions. All units are SVG viewBox coordinates.
const TOTAL_WEDGES = 10;      // regardless of durationMin
const HALVES_PER_WEDGE = 2;   // inner + outer bands
const TOTAL_HALVES = TOTAL_WEDGES * HALVES_PER_WEDGE;
const RING_OUTER = 46;
const RING_MID = 32;
const RING_INNER = 18;

// Colours (brief 2026-02):
const COLOR_MET = '#43A047';     // green — target met
const COLOR_MISSED = '#FFD100';  // yellow — target missed
const COLOR_STOPPED = '#DC2626'; // red — rower stopped (no strokes) during window
const COLOR_PENDING_FILL = 'transparent'; // ring is outline-only when pending
const COLOR_RING_STROKE = 'rgba(15, 23, 42, 0.35)';

export default function SessionGoalPie({
  workoutActive = false,
  workoutPaused = false,
  elapsedSec = 0,
  // Live metric values — polled every render
  currentSplitSec = 0,   // split in seconds / 500 m
  strokeRate = 0,        // SPM
  metersPerSecond = 0,   // m/s
  // Setup state (parent-owned so it survives tab navigation)
  goal,                  // { durationMin, targetType, targetValue }
  onGoalChange,          // (partial) => void
  onSessionComplete,     // called once when the last wedge closes
  onSetupCollapsedChange,
}) {
  const { durationMin, targetType, targetValue } = goal;
  const totalMinutes = Math.max(1, Math.min(90, Math.round(durationMin || 30)));
  // 20 half-chunks total. Each half is durationSec / 20 seconds long.
  const halfSec = Math.max(1, (totalMinutes * 60) / TOTAL_HALVES);

  // Per-half pass/fail results — index 0..19. undefined = not yet reached.
  // Values: 'met' | 'missed' | 'stopped' — see halfState() for the mapping.
  const [halfResults, setHalfResults] = useState([]);
  // Running per-second sum inside the current in-flight half. `activity`
  // flips true the moment we see a strokeRate>0 or metersPerSecond>0
  // sample — if the whole window elapses without one, the wedge is
  // painted red (rower stopped mid-row per iter-68 brief).
  // `wrap` counts how many full 20-half cycles the ring has completed —
  // each wrap resets the wedges so a rower can keep going past the
  // original target duration and see fresh segments fill in.
  const bucketRef = useRef({ halfIdx: 0, samples: 0, sum: 0, wrap: 0, activity: false });
  const [wrapCount, setWrapCount] = useState(0);

  // Reset when a fresh workout starts, or when the setup changes mid-idle.
  const prevActiveRef = useRef(false);
  useEffect(() => {
    if (workoutActive && !prevActiveRef.current) {
      setHalfResults([]);
      setWrapCount(0);
      bucketRef.current = { halfIdx: 0, samples: 0, sum: 0, wrap: 0, activity: false };
    }
    prevActiveRef.current = workoutActive;
  }, [workoutActive]);
  useEffect(() => {
    if (!workoutActive) {
      setHalfResults([]);
      setWrapCount(0);
      bucketRef.current = { halfIdx: 0, samples: 0, sum: 0, wrap: 0, activity: false };
    }
  }, [durationMin, targetType, targetValue, workoutActive]);

  // Sample the live metric every 1 s while the workout is active. When a
  // half-chunk boundary closes, average the samples, evaluate against
  // target, and push the result into halfResults.
  //
  // ── iter-61 fix: reads live metrics via a ref so the effect can be
  // set up ONCE per workout instead of being torn down on every prop
  // change. Previously deps included currentSplitSec/strokeRate/etc
  // which change 10× per second — the setInterval was destroyed and
  // rebuilt continuously and never actually fired, so `halfResults`
  // stayed empty and no wedge ever turned orange or grey.
  const metricsRef = useRef({
    currentSplitSec, strokeRate, metersPerSecond, elapsedSec, targetType, targetValue,
  });
  metricsRef.current = { currentSplitSec, strokeRate, metersPerSecond, elapsedSec, targetType, targetValue };
  useEffect(() => {
    if (!workoutActive || workoutPaused) return undefined;
    const interval = window.setInterval(() => {
      const m = metricsRef.current;
      // Wrap-around: once the rower's chosen duration has passed, the ring
      // resets and starts colouring wedges again from 0. Lets the goal ring
      // stay useful for rows that go long past the target duration.
      const rawHalfIdx = Math.floor(m.elapsedSec / halfSec);
      const currentWrap = Math.floor(rawHalfIdx / TOTAL_HALVES);
      const halfIdx = rawHalfIdx % TOTAL_HALVES;
      const b = bucketRef.current;
      let sample = null;
      if (m.targetType === 'split')  sample = m.currentSplitSec;
      if (m.targetType === 'rating') sample = m.strokeRate;
      if (m.targetType === 'speed')  sample = m.metersPerSecond;

      if (currentWrap !== b.wrap) {
        // Finalize the last half of the previous wrap so the very last
        // wedge doesn't stay grey by default when the rower crosses the
        // wrap boundary.
        if (b.samples >= 3 || !b.activity) {
          const avg = b.samples > 0 ? b.sum / b.samples : null;
          const state = halfState(m.targetType, avg, b.activity, m.targetValue);
          setHalfResults((prev) => {
            const next = prev.slice();
            next[b.halfIdx] = state;
            return next;
          });
        }
        // Reset wedges for the fresh lap and remember which wrap we're
        // now on so the next tick doesn't loop back into this branch.
        setHalfResults([]);
        setWrapCount(currentWrap);
        bucketRef.current = { halfIdx, samples: 0, sum: 0, wrap: currentWrap, activity: false };
        return;
      }

      if (halfIdx !== b.halfIdx) {
        // Emit a result for the CLOSED half. We always score — the
        // 'stopped' state is what carries the "rower quit mid-row"
        // signal so we can't gate on samples>=3 anymore.
        const avg = b.samples > 0 ? b.sum / b.samples : null;
        const state = halfState(m.targetType, avg, b.activity, m.targetValue);
        setHalfResults((prev) => {
          const next = prev.slice();
          next[b.halfIdx] = state;
          return next;
        });
        bucketRef.current = { halfIdx, samples: 0, sum: 0, wrap: currentWrap, activity: false };
      }
      // Activity gate — a stroke-rate OR speed > 0 tick means the rower
      // is still moving. Rating/speed target types read directly from
      // those; split target still needs the pass/fail sample.
      if (m.strokeRate > 0 || m.metersPerSecond > 0.3) {
        bucketRef.current.activity = true;
      }
      if (sample != null && Number.isFinite(sample) && sample > 0) {
        bucketRef.current.samples += 1;
        bucketRef.current.sum += sample;
      }
    }, 1000);
    return () => window.clearInterval(interval);
  }, [workoutActive, workoutPaused, halfSec]);

  // Fire onSessionComplete once when we've scored the final half.
  const completedFiredRef = useRef(false);
  useEffect(() => {
    if (!workoutActive) { completedFiredRef.current = false; return; }
    const filled = halfResults.filter((v) => v !== undefined).length;
    if (filled >= TOTAL_HALVES && !completedFiredRef.current) {
      completedFiredRef.current = true;
      onSessionComplete?.({ halves: halfResults.slice(0, TOTAL_HALVES) });
    }
  }, [halfResults, workoutActive, onSessionComplete]);

  // ── Setup panel auto-hide (matches Pace Target's 5 s pattern) ─────────
  // Default state per updated brief: the setup DOES NOT auto-open. The
  // rower sees the ring outline only until they tap it. Any tap inside
  // the setup re-arms a 5-second timer before the setup fades back to
  // the outline-only ring state.
  const [setupVisible, setSetupVisible] = useState(false);
  const setupTimerRef = useRef(null);
  const pokeSetup = () => {
    setSetupVisible(true);
    onSetupCollapsedChange?.(false);
    if (setupTimerRef.current) window.clearTimeout(setupTimerRef.current);
    setupTimerRef.current = window.setTimeout(() => {
      setSetupVisible(false);
      onSetupCollapsedChange?.(true);
    }, 5000);
  };
  useEffect(() => () => {
    if (setupTimerRef.current) window.clearTimeout(setupTimerRef.current);
  }, []);

  // ── Ring geometry — 10 annular sectors × 2 radial bands each ──────────
  const wedges = useMemo(() => {
    const arr = [];
    const step = 360 / TOTAL_WEDGES;
    for (let i = 0; i < TOTAL_WEDGES; i += 1) {
      const start = i * step;
      // Trim end angle slightly so separator lines stay crisp between
      // adjacent wedges.
      const end = (i + 1) * step - 1.2;
      // Inner band = first half of the chunk (halfResults[2i])
      // Outer band = second half of the chunk (halfResults[2i + 1])
      const inner = halfResults[i * 2];
      const outer = halfResults[i * 2 + 1];
      const colorFor = (r) => {
        if (r === 'met' || r === true) return COLOR_MET;
        if (r === 'stopped') return COLOR_STOPPED;
        if (r === 'missed' || r === false) return COLOR_MISSED;
        return COLOR_PENDING_FILL;
      };
      arr.push({
        i,
        innerD: annularSectorPath(50, 50, RING_INNER, RING_MID, start, end),
        outerD: annularSectorPath(50, 50, RING_MID, RING_OUTER, start, end),
        innerFill: colorFor(inner),
        outerFill: colorFor(outer),
      });
    }
    return arr;
  }, [halfResults]);

  const filledHalves = halfResults.filter((v) => v !== undefined).length;
  const passedHalves = halfResults.filter((v) => v === true || v === 'met').length;

  // Setup value stepper handlers
  const bumpDuration = (delta) => onGoalChange({ durationMin: Math.max(1, Math.min(90, totalMinutes + delta)) });
  const bumpValue = (delta) => {
    const step = targetType === 'split' ? 1 : targetType === 'rating' ? 1 : 0.1;
    const next = (Number(targetValue) || 0) + delta * step;
    onGoalChange({ targetValue: Math.max(0, next) });
  };
  const setType = (id) => {
    // Choose a sensible default when switching type.
    let val = targetValue;
    if (id === 'split' && (val < 60 || val > 400)) val = 130; // 2:10 /500m
    if (id === 'rating' && (val < 12 || val > 40)) val = 22;
    if (id === 'speed' && (val < 1 || val > 8)) val = 4.5;
    onGoalChange({ targetType: id, targetValue: val });
  };

  return (
    <div className="crewz-goal-wrap" data-testid="session-goal-pie" onClick={pokeSetup}>
      {/* Ring is outline-only until wedges start filling. Setup panel is
          hidden by default per the updated brief — a single tap on the
          ring reveals it. */}

      <div className="crewz-goal-pie" data-testid="session-goal-pie-svg">
        <svg viewBox="0 0 100 100" width="100%" height="100%" aria-label={`Session goal ${passedHalves} of ${TOTAL_HALVES} halves met`}>
          {wedges.map((w) => (
            <g key={w.i}>
              {/* Inner band — first half of chunk. Pending wedges use
                  a near-invisible fill so the whole ring stays
                  hit-testable for the tap-to-reveal interaction. */}
              <path
                d={w.innerD}
                fill={w.innerFill === 'transparent' ? 'rgba(148,163,184,0.06)' : w.innerFill}
                stroke={COLOR_RING_STROKE}
                strokeWidth="0.5"
                data-testid={`session-goal-wedge-${w.i}-inner`}
              />
              {/* Outer band — second half of chunk */}
              <path
                d={w.outerD}
                fill={w.outerFill === 'transparent' ? 'rgba(148,163,184,0.06)' : w.outerFill}
                stroke={COLOR_RING_STROKE}
                strokeWidth="0.5"
                data-testid={`session-goal-wedge-${w.i}-outer`}
              />
            </g>
          ))}
          {/* Full ring outline so pending state reads as a clean ring
              silhouette, not a bunch of loose stroked wedges. Drawn on
              top of the wedge fills so it always closes the shape. */}
          <circle cx="50" cy="50" r={RING_OUTER} fill="none" stroke={COLOR_RING_STROKE} strokeWidth="0.6" />
          <circle cx="50" cy="50" r={RING_INNER} fill="none" stroke={COLOR_RING_STROKE} strokeWidth="0.6" />
        </svg>
      </div>

      {/* Setup panel — visible ONLY when the ring has been tapped.
          Behaviour matches the Pace Target ghost control: any tap
          inside re-arms a 5-second auto-hide timer. */}
      {setupVisible && (
        <div className="crewz-goal-setup" data-testid="session-goal-setup" onClick={(e) => { e.stopPropagation(); pokeSetup(); }}>
          <div className="crewz-goal-heading">SESSION GOAL</div>
          {/* Duration stepper */}
          <div className="crewz-goal-row">
            <span className="crewz-goal-row-lbl">MIN</span>
            <button type="button" className="crewz-goal-step" data-testid="session-goal-dur-minus" onClick={() => bumpDuration(-1)} aria-label="Decrease duration">−</button>
            <span className="crewz-goal-row-val" data-testid="session-goal-dur-val">{totalMinutes}</span>
            <button type="button" className="crewz-goal-step" data-testid="session-goal-dur-plus" onClick={() => bumpDuration(1)} aria-label="Increase duration">+</button>
          </div>
          {/* Target type chips */}
          <div className="crewz-goal-types" data-testid="session-goal-types">
            {TARGET_TYPES.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`crewz-goal-chip ${targetType === t.id ? 'on' : ''}`}
                data-testid={`session-goal-type-${t.id}`}
                onClick={() => setType(t.id)}
              >
                {t.id === 'split' ? 'SPLIT' : t.id === 'rating' ? 'RATE' : 'SPEED'}
              </button>
            ))}
          </div>
          {/* Target value stepper */}
          <div className="crewz-goal-row">
            <span className="crewz-goal-row-lbl">TGT</span>
            <button type="button" className="crewz-goal-step" data-testid="session-goal-val-minus" onClick={() => bumpValue(-1)} aria-label="Decrease target">−</button>
            <span className="crewz-goal-row-val" data-testid="session-goal-val-val">{formatTargetValue(targetType, targetValue)}</span>
            <button type="button" className="crewz-goal-step" data-testid="session-goal-val-plus" onClick={() => bumpValue(1)} aria-label="Increase target">+</button>
          </div>
          {/* Progress readout — only inside the setup panel. Reads as
              "N of 20 halves met" so the rower can see the granular
              score without cluttering the ring. */}
          <div className="crewz-goal-readout" data-testid="session-goal-readout">
            <strong>{passedHalves}</strong>/<span>{TOTAL_HALVES}</span>
          </div>
        </div>
      )}
      {/* When the ring is showing outline-only, the whole `.crewz-goal-wrap`
          is the tap surface — no separate ghost button per updated brief
          ("Default state: the cf tile shows the ring outline only — no
          setup menu"). */}
      {workoutActive && filledHalves < TOTAL_HALVES && (
        <div className="crewz-goal-status" data-testid="session-goal-status" aria-hidden="true" />
      )}

      <style>{`
        .crewz-goal-wrap {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 4px;
          width: 100%;
          height: 100%;
          padding: 4px 3px;
          min-height: 0;
          min-width: 0;
          cursor: pointer;
        }
        .crewz-goal-heading {
          font-size: clamp(7px, 0.7vw, 8px);
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--c-dim, #64748B);
          text-align: center;
          line-height: 1;
        }
        .crewz-goal-pie {
          width: 100%;
          aspect-ratio: 1;
          max-height: 72px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .crewz-goal-readout {
          font-family: 'JetBrains Mono', ui-monospace, monospace;
          font-size: clamp(9px, 1.1vw, 12px);
          font-weight: 700;
          color: var(--c-fg, #0F172A);
          line-height: 1;
        }
        .crewz-goal-readout strong { color: #43A047; }
        .crewz-goal-setup {
          display: flex;
          flex-direction: column;
          gap: 3px;
          width: 100%;
          padding: 3px 0;
        }
        .crewz-goal-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 3px;
        }
        .crewz-goal-row-lbl {
          font-size: 7px;
          font-weight: 700;
          letter-spacing: 0.14em;
          color: var(--c-dim, #64748B);
        }
        .crewz-goal-row-val {
          font-family: 'JetBrains Mono', ui-monospace, monospace;
          font-size: 11px;
          font-weight: 700;
          min-width: 28px;
          text-align: center;
        }
        .crewz-goal-step {
          width: 18px;
          height: 18px;
          border-radius: 4px;
          border: 1px solid rgba(0,0,0,0.15);
          background: #fff;
          font-weight: 700;
          font-size: 12px;
          line-height: 1;
          cursor: pointer;
        }
        .crewz-goal-step:hover { background: #F1F5F9; }
        .crewz-goal-types { display: flex; gap: 2px; justify-content: center; }
        .crewz-goal-chip {
          padding: 2px 5px;
          border-radius: 999px;
          border: 1px solid rgba(0,0,0,0.15);
          background: #fff;
          font-size: 8px;
          font-weight: 800;
          letter-spacing: 0.06em;
          cursor: pointer;
        }
        .crewz-goal-chip.on {
          background: var(--marine-blue, #1565C0);
          color: #fff;
          border-color: var(--marine-blue, #1565C0);
        }
        .crewz-goal-ghost {
          font-size: 8px;
          font-weight: 700;
          letter-spacing: 0.14em;
          color: var(--marine-blue, #1565C0);
          background: transparent;
          border: none;
          cursor: pointer;
          text-transform: uppercase;
        }
        .crewz-goal-status {
          font-family: 'JetBrains Mono', ui-monospace, monospace;
          font-size: 9px;
          font-weight: 700;
          color: var(--c-dim, #64748B);
        }
        .crewz-cockpit.dark .crewz-goal-step {
          background: #1F2937;
          border-color: rgba(255,255,255,0.15);
          color: #F1F5F9;
        }
        .crewz-cockpit.dark .crewz-goal-step:hover { background: #374151; }
        .crewz-cockpit.dark .crewz-goal-chip {
          background: #1F2937;
          border-color: rgba(255,255,255,0.15);
          color: #F1F5F9;
        }
      `}</style>
    </div>
  );
}
